import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Divider, Input } from "@nextui-org/react";
import useSWR from "swr";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import { getFetcher } from "../../../../services/api";

/**
 * Riwayat Penghapusan Stok B3 - khusus Super Admin.
 *
 * Halaman ini satu-satunya tempat penghapusan stok bisa dilihat kembali: baris
 * stoknya sendiri sudah dibuang, yang tersisa hanya catatan di sini.
 *
 * Pencarian dikirim ke server (bukan disaring di browser) karena catatan
 * penghapusan menumpuk lintas perusahaan dan tidak dibatasi periode.
 */
export default function IndexRiwayatPenghapusanStokB3() {
  const [perusahaan, setPerusahaan] = useState("");
  const [casNumber, setCasNumber] = useState("");
  const [pencarian, setPencarian] = useState({ perusahaan: "", casNumber: "" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  /*
   * Ketikan ditunda sebentar sebelum jadi permintaan, supaya setiap huruf tidak
   * memicu satu panggilan ke server.
   */
  useEffect(() => {
    const jeda = setTimeout(() => {
      setPencarian({ perusahaan: perusahaan.trim(), casNumber: casNumber.trim() });
      setPage(0);
    }, 400);

    return () => clearTimeout(jeda);
  }, [perusahaan, casNumber]);

  const alamat = useMemo(() => {
    const parameter = new URLSearchParams({ returnAll: "true" });
    if (pencarian.perusahaan) parameter.set("perusahaan", pencarian.perusahaan);
    if (pencarian.casNumber) parameter.set("casNumber", pencarian.casNumber);
    return `/api/data-bahan-b3-company/riwayat-penghapusan?${parameter.toString()}`;
  }, [pencarian]);

  const { data, isLoading } = useSWR(alamat, getFetcher);

  const columns = [
    { field: "namaPerusahaan", headerName: "Perusahaan", minWidth: 220 },
    { field: "casNumber", headerName: "CAS Number", minWidth: 130 },
    { field: "namaBahanKimia", headerName: "Nama Bahan Kimia", minWidth: 200 },
    {
      field: "stokTerakhir",
      headerName: "Stok Sebelumnya (kg)",
      minWidth: 150,
      renderCell: (params) => `${params?.row?.stokTerakhir ?? 0}`,
    },
    { field: "alasan", headerName: "Alasan Penghapusan", minWidth: 300 },
    {
      field: "dihapusPada",
      headerName: "Waktu Penghapusan",
      minWidth: 220,
      renderCell: (params) => formatWaktu(params?.row?.dihapusPada),
    },
    {
      field: "dihapusOleh",
      headerName: "Dihapus Oleh",
      minWidth: 200,
      renderCell: (params) => params?.row?.dihapusOleh?.fullName || params?.row?.dihapusOleh?.email || "-",
    },
  ];

  return (
    <RootAdmin>
      <Card className="w-full mt-5" radius="sm">
        <CardHeader>Riwayat Penghapusan Stok B3</CardHeader>
        <Divider />
        <CardBody>
          <div className="pb-5 flex flex-col md:flex-row gap-4">
            <Input
              size="sm"
              label="Cari Perusahaan"
              labelPlacement="outside"
              placeholder="Nama perusahaan..."
              value={perusahaan}
              onValueChange={setPerusahaan}
              isClearable
              onClear={() => setPerusahaan("")}
              startContent={<MagnifyingGlassIcon className="size-4" />}
              className="md:max-w-sm"
            />
            <Input
              size="sm"
              label="Cari Bahan B3 (CAS Number)"
              labelPlacement="outside"
              placeholder="Contoh: 68-12-2"
              value={casNumber}
              onValueChange={setCasNumber}
              isClearable
              onClear={() => setCasNumber("")}
              startContent={<MagnifyingGlassIcon className="size-4" />}
              className="md:max-w-sm"
            />
          </div>

          <div className="h-[530px]">
            <ClientDataGrid
              rows={data?.data || []}
              columns={columns}
              rowCount={data?.total || 0}
              isLoading={isLoading}
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              clientPagination
            />
          </div>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}

/** Tanggal dan jam, keduanya diminta pada catatan penghapusan. */
function formatWaktu(nilai) {
  if (!nilai) return "-";

  return new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(nilai));
}
